import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { NotificationsNoneOutlined } from "@mui/icons-material";
import { Callout, PageHeader } from "@components/ui_v2";
import { RootState } from "@store/store";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import useInterval from "@hooks/useInterval";

const SECONDS_TO_FETCH = 10;

/**
 * Expediente de persona física en revisión mientras se preparan los contratos (`PF_Revisando` del diseño, paso
 * `ww-revisando` de WuWei). Sondea el estatus cada 10 s y navega automáticamente cuando el backend avanza.
 */
export const RevisandoExpedienteProcesando = () => {
  const navigate = useNavigate();
  const application = useSelector((state: RootState) => state.register.application);
  const loanStatus = useSelector((state: RootState) => state.register.loan_status);
  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();

  const statusPoll = useInterval(async () => {
    try {
      const loanApplication = await getLoanApplication(application.id).unwrap();
      if (loanApplication.current_status?.code && loanApplication.current_status.code !== loanStatus) {
        statusPoll.setIsActiveInterval(false);
        navigate(`/${loanApplication.current_status.url}`);
      }
    } catch (error) {
      console.error("Error al consultar el estatus de la solicitud", error);
    }
  }, SECONDS_TO_FETCH);

  useEffect(() => {
    statusPoll.setIsActiveInterval(true);
    return () => statusPoll.setIsActiveInterval(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
    <PageHeader
      eyebrow="Expediente · En revisión"
      title="Revisando tu expediente y preparando tus documentos"
      subtitle="Validaremos tu expediente y prepararemos los documentos contractuales para firma. Volveremos contigo a la brevedad."
    />

    {/* `.card.empty` del diseño: estado de proceso centrado. */}
    <Paper variant="lendia_paper" sx={{ textAlign: "center", p: "56px 24px" }}>
      {/* `.empty-art`: cuadro de 88px con el fondo de aviso de la marca y el spinner dentro. */}
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: "22px",
          bgcolor: "tokens.infoBg",
          border: "1px solid",
          borderColor: "tokens.infoBorder",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: "18px",
        }}
      >
        {/* `.spinner`: 36px con trazo de 3px; el anillo de fondo va con una sombra interior. */}
        <CircularProgress
          size={36}
          thickness={3.7}
          data-testid="revisando-expediente-spinner"
          sx={{
            color: "primary.main",
            borderRadius: "50%",
            boxShadow: (theme) => `inset 0 0 0 3px ${theme.palette.tokens?.infoBorder}`,
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 19, fontWeight: 600, color: "primary.dark", mb: 1 }}>
        Preparando tus documentos…
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 420, mx: "auto", lineHeight: 1.55 }}>
        Estamos validando tu información y generando tu contrato, tabla de amortización y pagaré.
      </Typography>
    </Paper>

    <Box sx={{ mt: 2 }}>
      <Callout tone="info" icon={<NotificationsNoneOutlined sx={{ fontSize: 14 }} />}>
        <strong>¡Despreocúpate!</strong> Puedes salir de esta pantalla. Tan pronto tu expediente se encuentre validado y
        tus documentos listos para firma, te contactaremos vía WhatsApp.
      </Callout>
    </Box>
  </Box>
  );
};
